import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../StateProvider/index.js';
import {
  sendVerificationCode,
  verifyTwilioCode,
  updateUserPhoneNumber,
  uploadFileToS3,
  addFamilyMember,
  updateStoreLocation,
  updateProfileComplete
} from '../../../../Utilities/api/index.js';
import './ProfileCompletePage.css';
import dogBreeds from '../../../../Utilities/data/dogBreeds.js';
import catBreeds from '../../../../Utilities/data/catBreeds.js';
import personPlaceholder from '../../../../../assets/Dashboard/Profile/personPlaceholder.png';
import addIcon from '../../../../../assets/Dashboard/Profile/addIcon.png';

function ProfileCompletePage() {
  var navigate = useNavigate();
  var ctx = useContext(StateContext);
  var user = ctx && ctx.user;
  var refreshUserData = ctx && ctx.refreshUserData;
  var setLoading = ctx && ctx.setLoading;

  const dobRef = useRef(null);

  // one-per-user guard so ensureOwnerMember doesn't rerun after verify/store refreshes
  const ownerEnsureRef = useRef({ uid: null, done: false });

  // profile fields
  var _useState = useState(''), phone = _useState[0], setPhone = _useState[1];
  var _useState2 = useState(''), newPhone = _useState2[0], setNewPhone = _useState2[1];
  var _useState3 = useState('idle'), verifyState = _useState3[0], setVerifyState = _useState3[1]; // idle | sending | code | verified
  var _useState4 = useState(''), verificationCode = _useState4[0], setVerificationCode = _useState4[1];
  var _useState5 = useState(''), selectedStore = _useState5[0], setSelectedStore = _useState5[1];
  var _useState6 = useState([]), errorMessages = _useState6[0], setErrorMessages = _useState6[1];

  // pets
  var _useState7 = useState(false), isAddingFamilyMember = _useState7[0], setIsAddingFamilyMember = _useState7[1];
  var _useState8 = useState(null), uploadedImage = _useState8[0], setUploadedImage = _useState8[1];
  var _useState9 = useState({ imageFile: null, vaccinationFile: null }), fileData = _useState9[0], setFileData = _useState9[1];
  var _useState10 = useState({
    userId: '',
    name: '',
    type: '',
    breed: 'Breed',
    dob: '',
    vaccination: '',
    image: ''
  }), memberDetails = _useState10[0], setMemberDetails = _useState10[1];

  // helper UI state
  var _useState11 = useState(''), petImageName = _useState11[0], setPetImageName = _useState11[1];
  var _useState12 = useState(''), vaccinationName = _useState12[0], setVaccinationName = _useState12[1];

  var placeholderImages = { Human: addIcon, Dog: addIcon, Cat: addIcon };
  function getPlaceholderImageUrl(type) { return placeholderImages[type] || addIcon; }

  // keep original state syncing on any user change
  useEffect(function () {
    if (!user) return;
    setMemberDetails(function (prev) {
      var next = Object.assign({}, prev);
      next.userId = user && user.uid ? user.uid : '';
      return next;
    });
    setPhone(user && user.info && user.info.phone ? user.info.phone : '');
    setSelectedStore(user && user.storeLocation ? user.storeLocation : '');
  }, [user]);

  // ensure owner ONCE per uid, after familyMembers is available
  useEffect(function () {
    if (!user || !user.uid) return;

    // reset guard if uid changed
    if (!ownerEnsureRef.current || ownerEnsureRef.current.uid !== user.uid) {
      ownerEnsureRef.current = { uid: user.uid, done: false };
    }

    // wait until familyMembers array exists
    var ready = user && user.familyMembers && Array.isArray(user.familyMembers);
    if (!ready) return;

    // only run once per uid
    if (ownerEnsureRef.current.done) return;
    ownerEnsureRef.current.done = true;

    ensureOwnerMember();
  }, [user]);

  async function ensureOwnerMember() {
    if (!user) return;
    var hasHuman = false;
    if (user && user.familyMembers && Array.isArray(user.familyMembers)) {
      for (var i = 0; i < user.familyMembers.length; i++) {
        var m = user.familyMembers[i];
        var t = m && m.type ? String(m.type).toLowerCase() : '';
        if (t === 'human') { hasHuman = true; break; }
      }
    }
    if (hasHuman) return;

    var ownerName = (user && user.info && (user.info.fullName || user.info.name))
      ? (user.info.fullName || user.info.name)
      : (user && user.displayName ? user.displayName : 'Owner');

    // Prefer server image first, then auth photo, else placeholder
    var ownerImage = personPlaceholder;
    if (user && user.image) {
      ownerImage = user.image;
    } else if (user && user.info && user.info.photoURL) {
      ownerImage = user.info.photoURL;
    }

    try {
      if (setLoading) setLoading(true);
      await addFamilyMember({
        userId: user.uid,
        name: ownerName,
        type: 'Human',
        breed: '',
        dob: null, // never send empty string for DATE
        vaccination: '',
        image: ownerImage,
        status: 'complete'
      });
      await Promise.resolve(refreshUserData(user.uid));
    } catch (e) {
      try { console.warn('ensureOwnerMember failed:', e && e.message ? e.message : e); } catch (_) {}
    } finally {
      if (setLoading) setLoading(false);
    }
  }

  function getBreedKeys(type) {
    var dict = null;
    if (type === 'Dog') dict = dogBreeds;
    else if (type === 'Cat') dict = catBreeds;

    var keys = [];
    if (dict) {
      for (var k in dict) {
        if (Object.prototype.hasOwnProperty.call(dict, k)) {
          keys.push(k);
        }
      }
    }
    keys.sort();
    return keys;
  }

  function openDobPicker() {
    var el = dobRef.current;
    if (!el) return;
    try { if (typeof el.showPicker === 'function') { el.showPicker(); return; } } catch (_) {}
    el.focus();
    try { el.click(); } catch (_) {}
  }

  // ---------- Helpers (logic only) ----------
  function normalizeUploadResult(res) {
    if (!res) return '';
    if (typeof res === 'string') return res;
    if (res.url) return res.url;
    if (res.Location) return res.Location;
    if (res.location) return res.location;
    if (res.data && res.data.url) return res.data.url;
    return '';
  }

  function ensureFileHasType(file) {
    try {
      if (!file || file.type) return file;
      var name = (file.name || '').toLowerCase();
      var ext = name.indexOf('.') !== -1 ? name.split('.').pop() : '';
      var map = {
        pdf:  'application/pdf',
        png:  'image/png',
        jpg:  'image/jpeg',
        jpeg: 'image/jpeg',
        heic: 'image/heic',
        heif: 'image/heif',
        webp: 'image/webp'
      };
      var guessed = map[ext];
      if (!guessed) return file;
      try {
        return new File([file], file.name, { type: guessed });
      } catch (_) {
        return file;
      }
    } catch (_) { return file; }
  }

  // ------- Handlers -------
  function onNewPhoneChange(e) { setNewPhone(e && e.target ? e.target.value : ''); }
  function onVerificationCodeChange(e) { setVerificationCode(e && e.target ? e.target.value : ''); }

  async function onStoreChange(e) {
    const value = e && e.target ? e.target.value : '';
    setSelectedStore(value);
    if (value) { await handleSaveStore(value); }
  }

  function onPetNameChange(e) {
    setMemberDetails(function (prev) { var n = Object.assign({}, prev); n.name = e && e.target ? e.target.value : ''; return n; });
  }
  function onPetTypeChange(e) {
    var val = e && e.target ? e.target.value : 'Dog';
    var validBreeds = getBreedKeys(val);
    var keep = validBreeds.indexOf(memberDetails.breed) !== -1;
    setMemberDetails(function (prev) {
      var n = Object.assign({}, prev);
      n.type = val;
      if (!keep) n.breed = '';
      return n;
    });
  }
  function onPetBreedChange(e) {
    var val = e && e.target ? e.target.value : 'Breed';
    setMemberDetails(function (prev) { var n = Object.assign({}, prev); n.breed = val; return n; });
  }
  function onPetDobChange(e) {
    var val = e && e.target ? e.target.value : '';
    setMemberDetails(function (prev) { var n = Object.assign({}, prev); n.dob = val; return n; });
  }

  // image picker
  function handleImageChange(e) {
    var f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!f) return;
    var fixed = ensureFileHasType(f);
    setFileData(function (prev) { var n = Object.assign({}, prev); n.imageFile = fixed; return n; });
    setUploadedImage(URL.createObjectURL(fixed));
    setPetImageName(fixed.name || '');
  }
  function clearPetImage() {
    setFileData(function (prev) { var n = Object.assign({}, prev); n.imageFile = null; return n; });
    setUploadedImage(null);
    setPetImageName('');
    try { var el = document.getElementById('pet-image'); if (el) el.value = ''; } catch (e) {}
  }

  // vaccination upload
  function handleVaccinationUpload(e) {
    var f = e && e.target && e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!f) return;
    var fixed = ensureFileHasType(f);
    setFileData(function (prev) { var n = Object.assign({}, prev); n.vaccinationFile = fixed; return n; });
    setVaccinationName(fixed.name || '');
  }
  function clearVaccination() {
    setFileData(function (prev) { var n = Object.assign({}, prev); n.vaccinationFile = null; return n; });
    setVaccinationName('');
    try { var el = document.getElementById('vaccination'); if (el) el.value = ''; } catch (e) {}
  }

  function startAddPet() { setIsAddingFamilyMember(true); }
  function cancelAddPet() {
    setIsAddingFamilyMember(false);
    clearPetImage();
    clearVaccination();
    setMemberDetails({
      userId: user && user.uid || '',
      name: '',
      type: 'Dog',
      breed: 'Breed',
      dob: '',
      vaccination: '',
      image: ''
    });
  }

  // Phone verify
  function handleSendCode() {
    if (!newPhone) return;
    setVerifyState('sending');
    sendVerificationCode(newPhone)
      .then(function () { setVerifyState('code'); })
      .catch(function () {
        setErrorMessages(['Could not send code. Try again.']);
        setVerifyState('idle');
      });
  }

  async function handleVerifyCode() {
    if (!verificationCode || !newPhone) return;
    if (setLoading) setLoading(true);
    try {
      const result = await verifyTwilioCode(newPhone, verificationCode, user.uid);
      if (result && result.success) {
        await updateUserPhoneNumber(user.uid, newPhone);
        await Promise.resolve(refreshUserData(user.uid));
        setPhone(newPhone);
        setVerifyState('verified');
      } else {
        setErrorMessages(['Verification failed. Please try again.']);
        setVerifyState('idle');
      }
    } catch (_) {
      setErrorMessages(['Something went wrong verifying your number.']);
      setVerifyState('idle');
    } finally {
      if (setLoading) setLoading(false);
    }
  }

  function resetPhoneFlow() { setVerifyState('idle'); setPhone(''); setNewPhone(''); }

  function formatPhoneNumber(num) {
    if (!num) return '';
    var digits = num.replace(/\D/g, '');
    if (digits.length === 10) {
      return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
    }
    return num;
  }

  // Store
  async function handleSaveStore(value) {
    var store = value || selectedStore;
    if (!store) return;
    if (setLoading) setLoading(true);
    try {
      await updateStoreLocation(user.uid, store);
      await Promise.resolve(refreshUserData(user.uid));
    } catch (_) {
      setErrorMessages(['Could not save store.']);
    } finally {
      if (setLoading) setLoading(false);
    }
  }

  // Add pet
  async function handleAddFamilyMemberSubmit() {
    if (!memberDetails.name || !memberDetails.type) {
      setErrorMessages(['Please enter your pet’s name and type.']);
      return;
    }
    if (setLoading) setLoading(true);
    setErrorMessages([]);

    try {
      // Image
      var imageUrl = getPlaceholderImageUrl(memberDetails.type);
      if (fileData.imageFile) {
        var imgRes = await uploadFileToS3(ensureFileHasType(fileData.imageFile));
        imageUrl = normalizeUploadResult(imgRes);
        if (!imageUrl) throw new Error('Image upload did not return a URL.');
      }

      // Vaccination (optional)
      var vaccinationUrl = memberDetails.vaccination || '';
      if (fileData.vaccinationFile) {
        var vacRes = await uploadFileToS3(ensureFileHasType(fileData.vaccinationFile));
        vaccinationUrl = normalizeUploadResult(vacRes);
        if (!vaccinationUrl) throw new Error('Vaccination upload did not return a URL.');
      }

      // Normalize DOB: never send empty string
      var dobToSend = (memberDetails && memberDetails.dob && memberDetails.dob !== 'Invalid date') ? memberDetails.dob : null;

      // Create pet
      await addFamilyMember({
        userId: memberDetails.userId,
        name: memberDetails.name,
        type: memberDetails.type,
        breed: memberDetails.breed,
        dob: dobToSend,
        vaccination: vaccinationUrl,
        image: imageUrl,
        status: 'incomplete'
      });

      await ensureOwnerMember();
      await Promise.resolve(refreshUserData(user.uid));

      setIsAddingFamilyMember(false);
      clearPetImage();
      clearVaccination();
      setMemberDetails({
        userId: user && user.uid || '',
        name: '',
        type: 'Dog',
        breed: 'Breed',
        dob: '',
        vaccination: '',
        image: ''
      });
    } catch (err) {
      var msg = (err && err.message) ? err.message : 'Could not add pet.';
      if (/vaccination/i.test(msg)) {
        setErrorMessages(['Could not add pet: vaccination file upload failed. Please try again or use a PDF/image under 10MB.']);
      } else if (/image upload/i.test(msg)) {
        setErrorMessages(['Could not add pet: photo upload failed.']);
      } else {
        setErrorMessages([msg]);
      }
      try { console.error('Add pet failed:', err); } catch (_) {}
    } finally {
      if (setLoading) setLoading(false);
    }
  }

  // Done
  async function handleDone() {
    try {
      if (setLoading) setLoading(true);
      await ensureOwnerMember();
      await Promise.resolve(refreshUserData(user.uid));

      var sourceUser = (ctx && ctx.user) ? ctx.user : (user || {});
      var hasPhone = !!(sourceUser && sourceUser.info && sourceUser.info.phone);
      var hasStore = !!selectedStore;
      var hasPet = false;
      if (sourceUser && sourceUser.familyMembers && Array.isArray(sourceUser.familyMembers)) {
        for (var i = 0; i < sourceUser.familyMembers.length; i++) {
          var m = sourceUser.familyMembers[i];
          var t = m && m.type ? String(m.type).toLowerCase() : '';
          if (t === 'dog' || t === 'cat') { hasPet = true; break; }
        }
      }

      if (hasPhone && hasStore && hasPet) {
        await updateProfileComplete(sourceUser.uid, true);
        navigate('/profile');
        return;
      }

      var missing = [];
      if (!hasPhone) missing.push('phone');
      if (!hasStore) missing.push('store');
      if (!hasPet) missing.push('a pet');
      setErrorMessages(['Please add ' + missing.join(', ') + ' to finish.']);
      try { window.scrollTo(0, 0); } catch (_) {}

    } catch (_) {
      setErrorMessages(['We couldn’t refresh your profile just now. Please try again.']);
    } finally {
      if (setLoading) setLoading(false);
    }
  }

  // ------- Build display list -------
  var rawMembers = (user && user.familyMembers && Array.isArray(user.familyMembers)) ? user.familyMembers : [];
  var hasHuman = false;
  for (var i = 0; i < rawMembers.length; i++) {
    var mm = rawMembers[i];
    var tt = mm && mm.type ? String(mm.type).toLowerCase() : '';
    if (tt === 'human') { hasHuman = true; break; }
  }

  var syntheticOwner = null;
  if (!hasHuman) {
    var sName = (user && user.info && (user.info.fullName || user.info.name))
      ? (user.info.fullName || user.info.name)
      : (user && user.displayName ? user.displayName : 'Owner');

    // Prefer server user.image, then auth photo
    var sImg = personPlaceholder;
    if (user && user.image) {
      sImg = user.image;
    } else if (user && user.info && user.info.photoURL) {
      sImg = user.info.photoURL;
    }

    syntheticOwner = { id: 'owner-synthetic', name: sName, type: 'Human', image: sImg };
  }

  var displayMembers = [];
  if (syntheticOwner) {
    displayMembers = [syntheticOwner].concat(rawMembers);
  } else {
    displayMembers = rawMembers.slice(0);
    displayMembers.sort(function (a, b) {
      var ta = a && a.type ? String(a.type).toLowerCase() : '';
      var tb = b && b.type ? String(b.type).toLowerCase() : '';
      if (ta === tb) return 0;
      if (ta === 'human') return -1;
      if (tb === 'human') return 1;
      return 0;
    });
  }

  // ------- UI -------
  return (
    <div className="profile-complete-page">
      <div className="profile-complete-header">
        <h1>Complete Your Profile</h1>
        <p>Welcome to Pawsible! Add your contact info, choose your home store, and introduce us to your pet.</p>
      </div>

      {/* Phone */}
      <div className="profile-complete-section">
        <h2>Phone Verification</h2>

        {verifyState === 'idle' && !phone ? (
          <div style={{ textAlign: 'center' }}>
            <input
              type="text"
              id="phone"
              value={newPhone}
              onChange={onNewPhoneChange}
              className="profile-complete-input"
              placeholder="Enter phone"
              inputMode="tel"
            />
            <div className="profile-complete-actions">
              <button className="profile-complete-btn primary" onClick={handleSendCode}>Send Code</button>
            </div>
          </div>
        ) : null}

        {verifyState === 'sending' ? (
          <p style={{ textAlign: 'center' }}>Sending verification code…</p>
        ) : null}

        {verifyState === 'code' ? (
          <div style={{ textAlign: 'center' }}>
            <input
              type="text"
              value={verificationCode}
              onChange={onVerificationCodeChange}
              className="profile-complete-input"
              placeholder="Enter code"
              inputMode="numeric"
            />
            <div className="profile-complete-actions">
              <button className="profile-complete-btn primary" onClick={handleVerifyCode}>Verify</button>
            </div>
          </div>
        ) : null}

        {(verifyState === 'verified' || phone) ? (
          <div className="profile-complete-actions">
            <span style={{ marginRight: 10, fontSize: '15px' }}>{formatPhoneNumber(phone)}</span>
            <button className="profile-complete-btn secondary" onClick={resetPhoneFlow}>Change</button>
          </div>
        ) : null}
      </div>

      {/* Store */}
      <div className="profile-complete-section">
        <h2>Home Store</h2>
        <div style={{ textAlign: 'center' }}>
          <select id="store" value={selectedStore} onChange={onStoreChange} className="profile-complete-input">
            <option value="">Select Location</option>
            <option value="DTLA">Downtown LA</option>
          </select>
        </div>
      </div>

      {/* Pack */}
      <div className="profile-complete-section">
        <h2>Your Pack</h2>
        {!isAddingFamilyMember ? (
          <div>
            <div className="profile-complete-pet-section">
              {displayMembers.map(function (member, index) {
                return (
                  <div key={member && member.id ? member.id : index} className="profile-complete-pet-card">
                    <img
                      className="profile-complete-pet-image"
                      src={(member && member.image) ? member.image : getPlaceholderImageUrl(member && member.type)}
                      alt={(member && member.type) ? (member.type + ' Placeholder') : 'Member Placeholder'}
                    />
                    <div className="profile-complete-pet-name">{member && member.name}</div>
                    <div className="profile-complete-pet-type">{member && member.type}</div>
                  </div>
                );
              })}
              <div className="profile-complete-pet-card" onClick={startAddPet}>
                <img className="profile-complete-pet-image" src={addIcon} alt="Add New" />
                <div className="profile-complete-pet-name">Add</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            {/* Hidden input for pet image */}
            <input
              id="pet-image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', border: 0, clip: 'rect(0 0 0 0)' }}
              aria-hidden="true"
              tabIndex={-1}
            />

            {/* Clickable avatar trigger */}
            <label htmlFor="pet-image" className="profile-complete-avatar-trigger" title="Upload pet photo">
              <img
                className="profile-complete-pet-image"
                style={{ width: 80, height: 80 }}
                src={uploadedImage || getPlaceholderImageUrl(memberDetails.type)}
                alt="Pet photo. Click to upload"
              />
              <div className="profile-complete-avatar-hint">Tap to add photo</div>
            </label>

            {petImageName ? (
              <div className="file-chip">{/* reserved */}</div>
            ) : null}

            <input
              type="text"
              className="profile-complete-input"
              id="name"
              value={memberDetails.name}
              onChange={onPetNameChange}
              placeholder="Pet Name"
            />

            <select
              id="type"
              className="profile-complete-input"
              value={memberDetails.type || ''}
              onChange={onPetTypeChange}
            >
              <option value="" disabled hidden>Type</option>
              <option value="Dog">Dog</option>
              <option value="Cat">Cat</option>
            </select>

            <select
              id="breed"
              value={memberDetails.breed || ''}
              className="profile-complete-input"
              onChange={onPetBreedChange}
            >
              <option value="Breed">Breed</option>
              {getBreedKeys(memberDetails.type).map(function (b) {
                return <option key={b} value={b}>{b}</option>;
              })}
            </select>

            <div className={'date-field ' + (memberDetails.dob ? 'has-value' : '')} data-placeholder="Date of birth">
              <input
                id="family-member-dob"
                type="date"
                className="profile-complete-input dob-input"
                ref={dobRef}
                value={memberDetails.dob}
                onChange={onPetDobChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Vaccination */}
            <div className="vaccination-file-control">
              <input
                id="vaccination"
                type="file"
                accept=".pdf,image/*"
                onChange={handleVaccinationUpload}
                style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', border: 0, clip: 'rect(0 0 0 0)' }}
                aria-hidden="true"
                tabIndex={-1}
              />
              <label htmlFor="vaccination" className="vaccination-file-label">Upload vaccination record</label>
              {vaccinationName ? (
                <div className="file-chip" style={{ marginTop: 8 }}>
                  <span className="file-chip-name">{vaccinationName}</span>
                  <button type="button" className="file-chip-action" onClick={clearVaccination}>Remove</button>
                  <label htmlFor="vaccination" className="file-chip-action as-label">Replace</label>
                </div>
              ) : (
                <p className="file-hint">PDF or image</p>
              )}
            </div>

            <div className="profile-complete-actions">
              <button className="profile-complete-btn primary" onClick={handleAddFamilyMemberSubmit}>Add Member</button>
              <button className="profile-complete-btn secondary" onClick={cancelAddPet}>Back</button>
            </div>
          </div>
        )}
      </div>

      {errorMessages.length > 0 ? (
        <div className="profile-complete-section" style={{ textAlign: 'center' }}>
          {errorMessages.map(function (msg, idx) {
            return <p key={idx} style={{ color: '#d8000c', fontWeight: 500, margin: 0 }}>{msg}</p>;
          })}
        </div>
      ) : null}

      {/* Footer actions */}
      <div className="profile-complete-actions">
        <button className="profile-complete-btn primary" onClick={handleDone}>Done</button>
        <button className="profile-complete-btn secondary" onClick={function () { navigate(-1); }}>Back</button>
      </div>
    </div>
  );
}

export default ProfileCompletePage;
